import React from "react";
import { Chip } from "@my-scoot/component-library-legacy";
import { OFFER_TYPE } from "ui/modules/LimitedOfferModalContent/constants";
import {
  TRIGGER_WEEKDAYS,
  TRIGGER_FROM_BACKEND,
  isSurgeRecord,
} from "ui/modules/LimitedOfferModalContent/utils/offerContract";
import {
  COUNT_DOWN_TRIGGERS,
  TRIGGER_TIER_META,
  isTimeProximityTrigger,
} from "ui/modules/LimitedOfferModalContent/utils/unifiedOffer";
import { getCurrencySymbol, MoneyField } from "./CustomColumns";

// Mirrors PRICE_REGION_CHOICES.INTERNATIONAL (= 1) without importing the
// heavy transactions constants module (it drags dataProvider into unit tests).
const INTERNATIONAL_REGION = 1;
const BATCH_FUTURE_UID = "__future__";

const isNum = (v) =>
  v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v));

const WEEKDAY_SHORT = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

const shortDay = (label) => WEEKDAY_SHORT[label] || label;

/** Form string trigger ("seats_filled") preferred; backend int falls back. */
const resolveTriggerType = (record = {}) => {
  if (record?.metadata?.trigger_type) return record.metadata.trigger_type;
  const raw = record?.trigger_type;
  if (typeof raw === "number" || /^\d+$/.test(String(raw ?? ""))) {
    return TRIGGER_FROM_BACKEND[Number(raw)] || null;
  }
  return raw || null;
};

const resolveValueUnit = (record = {}) =>
  record?.metadata?.value_unit || record?.trigger_config?.value_unit || null;

/** "+" for Surge (increase), "-" for Early Bird (decrease). */
const changeSign = (record = {}) => (isSurgeRecord(record) ? "+" : "-");

const formatChange = (value, valueUnit, record) => {
  const sign = changeSign(record);
  const unit =
    valueUnit ||
    (record?.metadata?.offer_type === OFFER_TYPE.PERCENTAGE
      ? "percent"
      : "flat");
  if (unit === "percent") return `${sign}${value}%`;
  return `${sign}${getCurrencySymbol(record)}${value}`;
};

/** "≥80%" (fills) vs "≤5 seats" (left / countdown). */
const formatCountCondition = (threshold, thresholdUnit, triggerType) => {
  const op = COUNT_DOWN_TRIGGERS.includes(triggerType) ? "≤" : "≥";
  if (thresholdUnit === "percent") return `${op}${threshold}%`;
  const suffix = (TRIGGER_TIER_META[triggerType]?.numberSuffix || "")
    .replace("#", "")
    .trim();
  return suffix ? `${op}${threshold} ${suffix}` : `${op}${threshold}`;
};

/**
 * One badge per tier/row: { key, condition, change }.
 * Covers every trigger shape from both `metadata` (form snapshot) and
 * `trigger_config` (backend-persisted — metadata may be dropped on create):
 * - count tiers: WHEN threshold reaches/drops → change by value
 * - time tiers: N days/hours before start → change by value
 * - vip: day/date + time range → markup
 * - batch: batch (or future default) → value
 */
export const getOfferTierBadges = (record = {}) => {
  const metadata = record?.metadata || {};
  const triggerConfig =
    record?.trigger_config && typeof record.trigger_config === "object"
      ? record.trigger_config
      : {};
  const triggerType = resolveTriggerType(record);
  const valueUnit = resolveValueUnit(record);
  const badges = [];
  const change = (value) => formatChange(value, valueUnit, record);

  // Count tiers ({threshold, value}) + time tiers ({before, value}).
  const metaTiers = Array.isArray(metadata.tiers) ? metadata.tiers : [];
  const cfgTiers = Array.isArray(triggerConfig.tiers)
    ? triggerConfig.tiers
    : [];
  const tiers = metaTiers.length ? metaTiers : cfgTiers;
  if (tiers.length) {
    const timeTrigger = isTimeProximityTrigger(triggerType);
    tiers.forEach((t, idx) => {
      if (!isNum(t?.value)) return;
      // Backend shape: {before, before_unit}. Form/metadata shape for time
      // triggers: {threshold, threshold_unit: "days"|"hours"}.
      // Backend count tiers carry threshold_unit at config level
      // (tiers hold only {threshold, value}).
      const thresholdUnit =
        t?.threshold_unit ||
        metadata.threshold_unit ||
        triggerConfig.threshold_unit;
      let condition;
      if (t?.before !== undefined && t?.before !== "") {
        condition = `${t.before} ${t?.before_unit || "days"} before`;
      } else if (timeTrigger && isNum(t?.threshold)) {
        condition = `${t.threshold} ${
          t?.threshold_unit || metadata.threshold_unit || "days"
        } before`;
      } else if (isNum(t?.threshold)) {
        condition = formatCountCondition(
          t.threshold,
          thresholdUnit,
          triggerType
        );
      } else {
        condition = `Tier ${idx + 1}`;
      }
      badges.push({
        key: `tier-${idx}`,
        condition,
        change: change(Number(t.value)),
      });
    });
    if (badges.length) return badges;
  }

  // VIP / priority slots.
  const vipWindows = Array.isArray(metadata.vip_windows)
    ? metadata.vip_windows
    : [];
  const applyBy = metadata.vip_apply_by;
  if (vipWindows.length) {
    vipWindows.forEach((w, idx) => {
      if (!isNum(w?.markup)) return;
      const day =
        applyBy === "date"
          ? w?.label || `Date ${idx + 1}`
          : shortDay(w?.label || `Day ${idx + 1}`);
      const ranges = Array.isArray(w?.ranges) ? w.ranges : [];
      if (!ranges.length) {
        badges.push({
          key: `vip-${idx}`,
          condition: day,
          change: change(Number(w.markup)),
        });
        return;
      }
      ranges.forEach((r, rIdx) => {
        const when = r?.start && r?.end ? `${day} ${r.start}-${r.end}` : day;
        badges.push({
          key: `vip-${idx}-${rIdx}`,
          condition: when,
          change: change(Number(w.markup)),
        });
      });
    });
    if (badges.length) return badges;
  }
  const schedules = Array.isArray(triggerConfig.schedules)
    ? triggerConfig.schedules
    : [];
  if (schedules.length) {
    const weekly = triggerConfig.interval_type !== 1;
    schedules.forEach((s, idx) => {
      if (!isNum(s?.value)) return;
      const day =
        weekly && Number.isInteger(Number(s?.interval))
          ? shortDay(TRIGGER_WEEKDAYS[Number(s.interval)] || String(s.interval))
          : String(s?.interval ?? `Slot ${idx + 1}`);
      const range = Array.isArray(s?.time_range) ? s.time_range : [];
      const when =
        range[0] && range[1] ? `${day} ${range[0]}-${range[1]}` : day;
      badges.push({
        key: `vip-${idx}`,
        condition: when,
        change: change(Number(s.value)),
      });
    });
    if (badges.length) return badges;
  }

  // Batch wise (rows + "all future batches" default). The list API
  // trigger_config carries no batch labels (uuids only), so rows render
  // as Batch 1/2/… in order; stored metadata labels win when present.
  const batchValues = Array.isArray(metadata.batch_values)
    ? metadata.batch_values
    : [];
  if (batchValues.length) {
    batchValues.forEach((b, idx) => {
      if (!isNum(b?.value)) return;
      const name =
        b?.batch_uid === BATCH_FUTURE_UID
          ? "Future batches"
          : b?.label || `Batch ${idx + 1}`;
      badges.push({
        key: `batch-${idx}`,
        condition: name,
        change: change(Number(b.value)),
      });
    });
    if (badges.length) return badges;
  }
  const rows = Array.isArray(triggerConfig.rows) ? triggerConfig.rows : [];
  rows.forEach((r, idx) => {
    if (!isNum(r?.value)) return;
    badges.push({
      key: `batch-${idx}`,
      condition: `Batch ${idx + 1}`,
      change: change(Number(r.value)),
    });
  });
  if (triggerConfig.default && isNum(triggerConfig.default.value)) {
    badges.push({
      key: "batch-default",
      condition: "Future batches",
      change: change(Number(triggerConfig.default.value)),
    });
  }

  return badges;
};

/**
 * Collect every tier/row value from a list record (values only).
 * Kept for compatibility; badges carry condition + signed change.
 * Returns { values: number[], valueUnit }.
 */
export const getOfferTierValues = (record = {}) => {
  const metadata = record?.metadata || {};
  const triggerConfig =
    record?.trigger_config && typeof record.trigger_config === "object"
      ? record.trigger_config
      : {};
  const valueUnit = resolveValueUnit(record);
  const values = [];
  const push = (v) => {
    if (isNum(v)) values.push(Number(v));
  };

  const metaTiers = Array.isArray(metadata.tiers) ? metadata.tiers : [];
  if (metaTiers.length) {
    metaTiers.forEach((t) => push(t?.value));
    if (values.length) return { values, valueUnit };
  }
  const cfgTiers = Array.isArray(triggerConfig.tiers)
    ? triggerConfig.tiers
    : [];
  if (cfgTiers.length) {
    cfgTiers.forEach((t) => push(t?.value));
    if (values.length) return { values, valueUnit };
  }

  const vipWindows = Array.isArray(metadata.vip_windows)
    ? metadata.vip_windows
    : [];
  if (vipWindows.length) {
    vipWindows.forEach((w) => push(w?.markup));
    if (values.length) return { values, valueUnit };
  }
  const schedules = Array.isArray(triggerConfig.schedules)
    ? triggerConfig.schedules
    : [];
  if (schedules.length) {
    schedules.forEach((s) => push(s?.value));
    if (values.length) return { values, valueUnit };
  }

  const batchValues = Array.isArray(metadata.batch_values)
    ? metadata.batch_values
    : [];
  if (batchValues.length) {
    batchValues.forEach((b) => push(b?.value));
    if (values.length) return { values, valueUnit };
  }
  const rows = Array.isArray(triggerConfig.rows) ? triggerConfig.rows : [];
  rows.forEach((r) => push(r?.value));
  if (triggerConfig.default && isNum(triggerConfig.default.value)) {
    push(triggerConfig.default.value);
  }

  return { values, valueUnit };
};

/**
 * Offer Price cell: tiered/conditional offers (no single price) render one
 * badge per tier — "condition → change" — via the existing Chip component;
 * single-price offers keep the existing percentage / MoneyField rendering.
 */
const OfferPriceBadges = ({ record = {} }) => {
  const badges = getOfferTierBadges(record);

  if (badges.length > 0) {
    return (
      <div className="tw-flex tw-flex-wrap tw-gap-1 tw-py-[10px]">
        {badges.map((b) => (
          <Chip key={b.key} label={`${b.condition} → ${b.change}`} />
        ))}
      </div>
    );
  }

  return record?.metadata?.offer_type === OFFER_TYPE.PERCENTAGE ? (
    <span>
      {`${record?.metadata?.discount_percentage}%${
        record?.region === INTERNATIONAL_REGION ? " (International)" : ""
      }`}
    </span>
  ) : (
    <MoneyField noRightMargin record={record} source="price" />
  );
};

export { OfferPriceBadges };
export default OfferPriceBadges;
